import filter from 'lodash/filter';
import map from 'lodash/map';
import type { TiptapJSON } from '@ai-content-hub/content-kit';
import type {
    CompositeQuestionSection,
    CompositeQuestionsSidebarRow,
} from '../schema/compositeQuestionLayout';

/** @deprecated Use {@link CompositeQuestionSection} from schema. */
export type CompositeEditQuestionSidebarSection = CompositeQuestionSection;

/** @deprecated Use {@link CompositeQuestionsSidebarRow} from schema. */
export type CompositeEditQuestionSidebarRow = CompositeQuestionsSidebarRow;

export function compositeEditQuestionSidebarSortableId(
    row: CompositeEditQuestionSidebarRow,
    index?: number,
): string {
    if (row.kind === 'section') return `__section:${row.section.id}`;
    if (row.kind === 'break') return `__break:${index ?? 0}`;
    return row.itemId;
}

function tiptapNodeToPlainText(node: unknown): string {
    if (node == null) {
        return '';
    }
    if (typeof node === 'string') {
        return node;
    }
    if (Array.isArray(node)) {
        return map(node, tiptapNodeToPlainText).join(' ').trim();
    }
    if (typeof node !== 'object') {
        return '';
    }
    const n = node as Record<string, unknown>;
    if (typeof n.text === 'string') {
        return n.text;
    }
    if (n.content != null) {
        return tiptapNodeToPlainText(n.content);
    }
    return '';
}

/** Plain text from a Tiptap JSON document (for labels / previews). */
export function tiptapDocToPlainText(doc: TiptapJSON | undefined | null): string {
    return tiptapNodeToPlainText(doc).replace(/\s+/g, ' ').trim();
}

/** Truncated plain text for sidebar section description preview. */
export function sectionSidebarDescriptionPreview(
    doc: TiptapJSON | undefined | null,
    maxLength = 220,
): string {
    const plain = tiptapDocToPlainText(doc);
    if (!plain) {
        return '';
    }
    if (plain.length <= maxLength) {
        return plain;
    }
    return `${plain.slice(0, maxLength - 1)}…`;
}

/** True when the item row at this index follows a section with no break in between. */
export function isSidebarItemNestedUnderSection(
    rows: readonly CompositeEditQuestionSidebarRow[],
    itemRowIndex: number,
): boolean {
    const row = rows[itemRowIndex];
    if (!row || row.kind !== 'item') {
        return false;
    }
    let j = itemRowIndex - 1;
    while (j >= 0 && rows[j].kind === 'item') {
        j -= 1;
    }
    // A 'break' row terminates the previous section — items after it are root-level.
    if (j >= 0 && rows[j].kind === 'break') {
        return false;
    }
    return j >= 0 && rows[j].kind === 'section';
}

export function deriveDefaultQuestionsSidebarRows(
    itemIds: readonly string[],
): CompositeEditQuestionSidebarRow[] {
    return map(itemIds, (itemId) => ({ kind: 'item' as const, itemId }));
}

export function reconcileQuestionsSidebarRows(
    rows: CompositeEditQuestionSidebarRow[] | undefined | null,
    itemIds: readonly string[],
): CompositeEditQuestionSidebarRow[] {
    const r = rows ?? [];
    if (r.length === 0) {
        return deriveDefaultQuestionsSidebarRows(itemIds);
    }
    const idSet = new Set(itemIds);
    const scrubbed = filter(
        r,
        (row) => row.kind === 'section' || row.kind === 'break' || idSet.has(row.itemId),
    );
    const present = new Set<string>();
    for (const row of scrubbed) {
        if (row.kind === 'item') {
            present.add(row.itemId);
        }
    }
    const toAppend = filter([...itemIds], (id) => !present.has(id));
    return [...scrubbed, ...map(toAppend, (itemId) => ({ kind: 'item' as const, itemId }))];
}

export function resolvedQuestionsSidebarRows(
    rows: CompositeEditQuestionSidebarRow[] | undefined | null,
    itemIds: readonly string[],
): CompositeEditQuestionSidebarRow[] {
    const r = rows ?? [];
    if (r.length === 0) {
        return deriveDefaultQuestionsSidebarRows(itemIds);
    }
    return reconcileQuestionsSidebarRows(r, itemIds);
}

export function orderItemsByQuestionsSidebarRows<T extends { id: string }>(
    items: readonly T[],
    rows: readonly CompositeEditQuestionSidebarRow[],
): T[] {
    const byId = new Map<string, T>(map(items, (it) => [it.id, it] as [string, T]));
    const ordered: T[] = [];
    const seen = new Set<string>();
    for (const row of rows) {
        if (row.kind !== 'item') {
            continue;
        }
        const it = byId.get(row.itemId);
        if (it) {
            ordered.push(it);
            seen.add(row.itemId);
        }
    }
    for (const it of items) {
        if (!seen.has(it.id)) {
            ordered.push(it);
        }
    }
    return ordered;
}

export type CompositeQuestionSidebarExerciseGroup =
    | { kind: 'root'; exerciseIds: string[] }
    | {
          kind: 'section';
          section: CompositeEditQuestionSidebarSection;
          exerciseIds: string[];
      };

/**
 * Partition item ids following editor sidebar semantics: items after a section row belong to that
 * section until the next section; leading items with no section above are {@link kind} `root`.
 */
export function buildQuestionSidebarExerciseGroups(
    rows: readonly CompositeEditQuestionSidebarRow[],
): CompositeQuestionSidebarExerciseGroup[] {
    const groups: CompositeQuestionSidebarExerciseGroup[] = [];
    let rootBuffer: string[] = [];
    let activeSection: CompositeEditQuestionSidebarSection | null = null;
    let sectionBuffer: string[] = [];

    const flushRoot = () => {
        if (rootBuffer.length > 0) {
            groups.push({ kind: 'root', exerciseIds: [...rootBuffer] });
            rootBuffer = [];
        }
    };

    const flushSection = () => {
        if (activeSection !== null) {
            groups.push({
                kind: 'section',
                section: activeSection,
                exerciseIds: [...sectionBuffer],
            });
            activeSection = null;
            sectionBuffer = [];
        }
    };

    for (const row of rows) {
        if (row.kind === 'section') {
            flushRoot();
            flushSection();
            activeSection = row.section;
            sectionBuffer = [];
        } else if (row.kind === 'break') {
            // A break terminates the current section and returns to root mode.
            flushSection();
        } else {
            if (activeSection !== null) {
                sectionBuffer.push(row.itemId);
            } else {
                rootBuffer.push(row.itemId);
            }
        }
    }

    flushSection();
    flushRoot();

    return groups;
}

/**
 * When there are no section headers, sidebar order follows `items` / `exercises`
 * and the field can be omitted from persisted payloads.
 */
export function persistQuestionsSidebarRows(
    rows: readonly CompositeEditQuestionSidebarRow[],
): CompositeEditQuestionSidebarRow[] | undefined {
    if (!rows.some((r) => r.kind === 'section' || r.kind === 'break')) {
        return undefined;
    }
    return [...rows];
}
